import { Button, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { translate as t } from 'hooks/useTranslations';
import { PhxBadge } from '_lex/_atoms/PhxBadge';
import { ActionMenu } from 'components/_atoms';
import { Edit } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import { useSnackbar } from 'notistack';
import { ReleaseNotes } from 'views/App/Portal/Policy/_types/manuals/draft';

interface StatusChipProps {
  manualLevel?: string;
  isEditing?: boolean;
  draftState?: ReleaseNotes['draftState'];
  onApproveReleaseNotes?: () => Promise<void>;
}

const StatusChip = ({ manualLevel, isEditing, draftState, onApproveReleaseNotes }: StatusChipProps) => {
  const [approving, setApproving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleApprove = async () => {
    setApproving(true);
    try {
      if (onApproveReleaseNotes) {
        await onApproveReleaseNotes();
        enqueueSnackbar(
          t('draftManuals.releaseNotes.statusSuccess', '', {
            status: t('draftManuals.releaseNotes.approved'),
          }),
          { variant: 'success' }
        );
      }
    } catch (e: any) {
      console.error('Release Note approve error:', e);
      enqueueSnackbar(
        e?.message
          ? `${t('draftManuals.releaseNotes.statusError')}: ${e.message}`
          : `${t('draftManuals.releaseNotes.statusError')}: ${typeof e === 'string' ? e : JSON.stringify(e)}`,
        { variant: 'error' }
      );
    } finally {
      setApproving(false);
    }
  };

  return (
    <>
      {manualLevel !== 'AGENCY' &&
        (isEditing ? (
          draftState !== 'APPROVED' ? (
            <ActionMenu
              id='release-notes-status-menu'
              btnText={t(`draftManuals.releaseNotes.status.${draftState}`)}
              btnProps={{
                variant: 'outlined',
                color: 'neutral',
                size: 'sm',
                startIcon: approving ? (
                  <CircularProgress
                    size={20}
                    color='inherit'
                  />
                ) : (
                  <Edit />
                ),
                disabled: approving,
              }}
              items={[
                {
                  key: 'APPROVED',
                  label: t('draftManuals.releaseNotes.approve'),
                  icon: <CheckIcon />,
                  onClick: handleApprove,
                  disabled: approving,
                },
              ]}
            />
          ) : (
            <Button
              variant='outlined'
              color='neutral'
              startIcon={<CheckIcon />}
              sx={{
                width: 140,
                height: 32,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: 'background.paper',
                color: 'text.primary',
                border: '1.5px solid',
                borderColor: 'divider',
                justifyContent: 'flex-start',
                px: 2,
                alignItems: 'center',
                display: 'flex',
              }}
              disabled
            >
              {t('draftManuals.releaseNotes.approved')}
            </Button>
          )
        ) : (
          <PhxBadge
            color={draftState === 'APPROVED' ? 'success' : 'neutral'}
            size='sm'
          >
            {t(`draftManuals.releaseNotes.status.title`)}: {t(`draftManuals.releaseNotes.status.${draftState}`)}
          </PhxBadge>
        ))}
    </>
  );
};

export default StatusChip;
